import React, { useState, useEffect, useCallback, useRef } from 'react'
import API from '../../../service/api'
import './PushMonitor.css'

const formatAge = (seconds) => {
  if (seconds === null || seconds === undefined) return '-'
  if (seconds < 60) return seconds + 's'
  if (seconds < 3600) return Math.floor(seconds / 60) + 'm ' + (seconds % 60) + 's'
  return Math.floor(seconds / 3600) + 'h ' + Math.floor((seconds % 3600) / 60) + 'm'
}

const formatTime = (isoStr) => {
  if (!isoStr) return '-'
  const d = new Date(isoStr)
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

const ageClass = (sec) => {
  if (sec === null) return ''
  if (sec <= 300) return 'pm-age-ok'
  if (sec <= 1800) return 'pm-age-warn'
  return 'pm-age-stale'
}

const PushMonitor = () => {
  const [clients, setClients] = useState([])
  const [selectedClient, setSelectedClient] = useState('')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const intervalRef = useRef(null)

  // Fetch client list
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await API({ url: '/push-monitor/clients', method: 'GET', manageLoader: false })
        const list = res?.data?.data || []
        setClients(list)
        if (list.length > 0) {
          setSelectedClient(prev => prev || list[0].clientCode)
        }
      } catch (err) {
        console.error('Failed to fetch clients', err)
      }
    }
    fetchClients()
  }, [])

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    if (!selectedClient) return
    try {
      setLoading(true)
      setError(null)
      const res = await API({ url: `/push-monitor/dashboard?clientCode=${selectedClient}`, method: 'GET', manageLoader: false })
      if (res?.data?.success) {
        setData(res.data.data)
      } else {
        setError(res?.data?.error || 'Failed to load')
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [selectedClient])

  // Fetch on client change
  useEffect(() => {
    if (selectedClient) fetchDashboard()
  }, [selectedClient, fetchDashboard])

  // Auto-refresh every 30s
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    if (autoRefresh && selectedClient) {
      intervalRef.current = setInterval(fetchDashboard, 30000)
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [autoRefresh, selectedClient, fetchDashboard])

  const summary = data?.summary
  const vehicles = data?.vehicles || []
  const timeline = data?.pushTimeline || []

  return (
    <div className="pm-container">
      <div className="pm-header">
        <h5 className="pm-title">Push API Monitor</h5>
        {summary && <span className="pm-last-check">Last checked: {formatTime(summary.checkedAt)}</span>}
      </div>

      <div className="pm-toolbar">
        <select className="pm-select" value={selectedClient} onChange={e => setSelectedClient(e.target.value)}>
          <option value="">Select Client</option>
          {clients.map(c => (
            <option key={c.clientCode} value={c.clientCode}>{c.clientCode} ({c.serviceId})</option>
          ))}
        </select>
        <button className="pm-refresh-btn" onClick={fetchDashboard} disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
        <label className="pm-auto-label">
          <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
          Auto-refresh (30s)
        </label>
      </div>

      {error && <div className="pm-error">{error}</div>}
      {loading && !data && <div className="pm-loading">Loading dashboard...</div>}

      {summary && (
        <>
          {/* Summary Cards */}
          <div className="pm-summary">
            <div className="pm-card pm-card-blue">
              <p className="pm-card-value">{summary.totalVehicles}</p>
              <p className="pm-card-label">Total Vehicles</p>
            </div>
            <div className="pm-card pm-card-green">
              <p className="pm-card-value">{summary.greenCount}</p>
              <p className="pm-card-label">Healthy</p>
            </div>
            <div className="pm-card pm-card-yellow">
              <p className="pm-card-value">{summary.yellowCount}</p>
              <p className="pm-card-label">Warning</p>
            </div>
            <div className="pm-card pm-card-red">
              <p className="pm-card-value">{summary.redCount}</p>
              <p className="pm-card-label">Critical</p>
            </div>
            <div className="pm-card pm-card-green">
              <p className="pm-card-value">{summary.pushSuccessLastHour}</p>
              <p className="pm-card-label">Push Success (1h)</p>
            </div>
            <div className="pm-card pm-card-red">
              <p className="pm-card-value">{summary.pushFailLastHour}</p>
              <p className="pm-card-label">Push Failed (1h)</p>
            </div>
            <div className="pm-card pm-card-gray">
              <p className="pm-card-value">{summary.totalSkipsLastHour}</p>
              <p className="pm-card-label">Skipped (1h)</p>
            </div>
            <div className="pm-card pm-card-gray">
              <p className="pm-card-value">{summary.authErrorLastHour}</p>
              <p className="pm-card-label">Auth Errors (1h)</p>
            </div>
          </div>

          {/* Vehicle Table */}
          <div className="pm-table-wrapper">
            <table className="pm-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Vehicle No</th>
                  <th>Health</th>
                  <th>Gap Reason</th>
                  <th>OEM / Model</th>
                  <th>GPS Age</th>
                  <th>CAN Age</th>
                  <th>GPS Time</th>
                  <th>Lat</th>
                  <th>Lng</th>
                  <th>SOC</th>
                  <th>Speed</th>
                  <th>Odometer</th>
                  <th>Ignition</th>
                  <th>Skips (1h)</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.map((v, i) => (
                  <tr key={v.vehicleNo}>
                    <td>{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>{v.vehicleNo}</td>
                    <td>
                      <span className={`pm-health-dot pm-dot-${v.health}`}></span>
                      {v.health}
                    </td>
                    <td>
                      {v.gapReason
                        ? <span className="pm-gap-tag pm-gap-red">{v.gapReason}</span>
                        : <span className="pm-gap-tag pm-gap-none">OK</span>
                      }
                    </td>
                    <td>{v.oem || '-'} / {v.model || '-'}</td>
                    <td className={ageClass(v.secsSinceGps)}>{formatAge(v.secsSinceGps)}</td>
                    <td className={ageClass(v.secsSinceCan)}>{formatAge(v.secsSinceCan)}</td>
                    <td>{formatTime(v.gpsTimestamp)}</td>
                    <td>{v.lat?.toFixed(4) || '-'}</td>
                    <td>{v.lng?.toFixed(4) || '-'}</td>
                    <td>{v.soc !== null ? v.soc.toFixed(0) + '%' : '-'}</td>
                    <td>{v.speed !== null ? v.speed.toFixed(0) + ' km/h' : '-'}</td>
                    <td>{v.odometer !== null ? v.odometer.toFixed(0) : '-'}</td>
                    <td>{v.ignition === true ? 'ON' : v.ignition === false ? 'OFF' : '-'}</td>
                    <td>{v.skippedCountLastHour || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Push Timeline */}
          {timeline.length > 0 && (
            <>
              <p className="pm-timeline-title">Push Timeline (last 20)</p>
              <div className="pm-timeline">
                {timeline.map((t, i) => (
                  <div key={i} className={`pm-tl-item ${t.status === 'SUCCESS' ? 'pm-tl-success' : 'pm-tl-fail'}`}>
                    <span className="pm-tl-time">{formatTime(t.time)}</span>
                    {t.status} ({t.vehicleCount}v)
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Config Info */}
          <div style={{ fontSize: 11, color: '#999', marginTop: 10 }}>
            ServiceId: {summary.serviceId} | Transmit Freq: {summary.transmitFreqSec}s | In TMS: {summary.vehiclesInTmsCurrent}/{summary.totalVehicles}
          </div>
        </>
      )}
    </div>
  )
}

export default PushMonitor
